import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { isAdminLikeRole } from '@/lib/roles';
import { useMyAdminMenuPermissions } from '@/hooks/useAdminMenuPermissions';
import { ADMIN_MENU_ITEMS } from '@/pages/admin/dashboard/menuItems';
import PageLoader from '@/components/base/PageLoader';

interface AdminAccessGuardProps {
  /** 許可されていない場合のリダイレクト先 */
  redirectTo: string;
}

/**
 * 管理エリア（/admin/* ほか）へのアクセスを制御するガード。
 *
 * - 管理系ロール（職員・事業所管理者・福祉事業管轄・総合管理者）は従来どおり全許可。
 * - 利用者（welfare_user）は、**個別に許可された管理メニューのページだけ**開ける
 *   （`admin_menu_user_permissions` の is_visible=true に対応するパス）。
 * - それ以外（一般社員など）はリダイレクト。
 *
 * 利用者の個別許可は「許可した項目のページを開けるようにする」ためのもので、
 * データの可視範囲は各テーブルの RLS に従う。
 */
export default function AdminAccessGuard({ redirectTo }: AdminAccessGuardProps) {
  const { profile, loading } = useAuth();
  const location = useLocation();

  const isEndUser = profile?.role === 'welfare_user';
  const { rows, loading: permLoading } = useMyAdminMenuPermissions(isEndUser);

  if (loading) {
    return <PageLoader />;
  }

  if (!profile) {
    return <Navigate to={redirectTo} replace />;
  }

  // 管理系ロールは全許可
  if (isAdminLikeRole(profile.role)) {
    return <Outlet />;
  }

  // 利用者は「個別許可された管理メニューのページ」だけ開ける
  if (isEndUser) {
    if (permLoading) {
      return <PageLoader />;
    }
    const grantedPaths = new Set(
      rows
        .filter((row) => row.is_visible)
        .map((row) => ADMIN_MENU_ITEMS.find((item) => item.key === row.menu_key)?.path)
        .filter((path): path is string => Boolean(path))
        .map((path) => path.split('?')[0]),
    );
    if (grantedPaths.has(location.pathname)) {
      return <Outlet />;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <Navigate to={redirectTo} replace />;
}